import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import AdminLayout from '@/components/Layout';
import { CalendarDays, Plus, Trash2, RefreshCw, X, ExternalLink, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';

const TYPE_OPTIONS = [
  { value: 'xmltv', label: 'XMLTV' },
];

const STATUS_META = {
  idle:    { label: 'Bekliyor',  color: 'text-gray-400 bg-gray-500',     Icon: AlertCircle },
  syncing: { label: 'Sync...',   color: 'text-amber-400 bg-amber-500',   Icon: Loader2 },
  ok:      { label: 'Güncel',    color: 'text-green-500 bg-green-500',   Icon: CheckCircle2 },
  error:   { label: 'Hata',      color: 'text-red-500 bg-red-500',       Icon: AlertCircle },
};

const EMPTY = { name: '', url: '', type: 'xmltv' };

export default function Epg() {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [syncingId, setSyncingId] = useState(null);

  const load = async () => {
    try {
      const res = await api.get('/epg-sources');
      setSources(res.data);
    } catch (err) {
      console.error('EPG yüklenemedi:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await api.post('/epg-sources', form);
      setShowAdd(false);
      setForm(EMPTY);
      load();
    } catch {
      alert('EPG kaynağı eklenemedi.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu EPG kaynağını silmek istediğinize emin misiniz? Bu kaynağı kullanan kanalların EPG bağlantısı temizlenecek.')) return;
    try {
      await api.delete(`/epg-sources/${id}`);
      setSources(sources.filter(s => s._id !== id));
    } catch {
      alert('Silinemedi.');
    }
  };

  const handleSync = async (id) => {
    setSyncingId(id);
    // Optimistic UI: durumu syncing'e çevir
    setSources(prev => prev.map(s => s._id === id ? { ...s, status: 'syncing' } : s));
    try {
      const res = await api.post(`/epg-sources/${id}/sync`);
      setSources(prev => prev.map(s =>
        s._id === id
          ? { ...s, status: 'ok', last_update: res.data?.last_update || new Date().toISOString() }
          : s
      ));
    } catch {
      setSources(prev => prev.map(s => s._id === id ? { ...s, status: 'error' } : s));
      alert('Sync başarısız.');
    } finally {
      setSyncingId(null);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' });
    } catch { return '—'; }
  };

  const maskUrl = (url) => {
    if (!url) return '—';
    try {
      const u = new URL(url);
      return `${u.protocol}//${u.hostname}${u.pathname.length > 12 ? u.pathname.slice(0, 12) + '…' : u.pathname}`;
    } catch { return url; }
  };

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-white flex items-center gap-3" data-testid="epg-page-title">
            <CalendarDays className="text-amber-500" /> EPG Kaynakları
          </h1>
          <button
            onClick={() => setShowAdd(true)}
            className="bg-amber-500 hover:bg-amber-600 text-black px-4 py-2 rounded-lg font-bold flex items-center gap-2 transition-transform active:scale-95"
            data-testid="epg-add-btn"
          >
            <Plus size={20} /> Yeni Kaynak
          </button>
        </div>

        <div className="bg-amber-500/5 border border-amber-700/30 rounded-xl p-4 mb-6 flex items-start gap-3 text-xs text-amber-300/80">
          <AlertCircle size={16} className="text-amber-400 mt-0.5 shrink-0" />
          <div>
            <strong className="text-amber-400">Phase 2 hazırlığı:</strong> "Yenile" butonu şu an <strong>stub</strong>'tır — yalnızca son güncelleme zamanını işaretler.
            Gerçek XMLTV parsing ve channel-EPG eşleme cron job'u sonraki fazda eklenecek.
          </div>
        </div>

        <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden shadow-2xl">
          <table className="w-full text-left text-white" data-testid="epg-table">
            <thead className="bg-slate-950 text-gray-400 text-xs uppercase font-bold tracking-wider">
              <tr>
                <th className="p-4">Kaynak Adı</th>
                <th className="p-4">URL</th>
                <th className="p-4">Tip</th>
                <th className="p-4">Durum</th>
                <th className="p-4">Son Güncelleme</th>
                <th className="p-4 text-right">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {loading ? (
                <tr><td colSpan="6" className="p-12 text-center text-gray-500 italic">Yükleniyor…</td></tr>
              ) : sources.length === 0 ? (
                <tr>
                  <td colSpan="6" className="p-12 text-center text-gray-500 italic" data-testid="epg-empty">
                    Henüz EPG kaynağı yok. "Yeni Kaynak" butonuna tıklayın.
                  </td>
                </tr>
              ) : (
                sources.map(src => {
                  const meta = STATUS_META[src.status] || STATUS_META.idle;
                  const Icon = meta.Icon;
                  const [textCls, dotCls] = meta.color.split(' ');
                  const isSyncing = syncingId === src._id || src.status === 'syncing';
                  return (
                    <tr key={src._id} className="hover:bg-slate-800/30 transition-colors group" data-testid={`epg-row-${src._id}`}>
                      <td className="p-4 font-bold text-white">{src.name}</td>
                      <td className="p-4">
                        <a href={src.url} target="_blank" rel="noopener noreferrer" className="font-mono text-xs text-gray-400 hover:text-amber-400 truncate flex items-center gap-1 max-w-[280px]">
                          {maskUrl(src.url)} <ExternalLink size={11} className="shrink-0 text-gray-600" />
                        </a>
                      </td>
                      <td className="p-4">
                        <span className="text-[10px] font-mono px-2 py-1 bg-slate-800 text-amber-400 rounded border border-slate-700 uppercase tracking-wider">
                          {src.type || 'xmltv'}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase ${textCls}`}>
                          <Icon size={11} className={isSyncing ? 'animate-spin' : ''} />
                          {meta.label}
                        </span>
                      </td>
                      <td className="p-4 text-gray-400 font-mono text-xs">{formatDate(src.last_update)}</td>
                      <td className="p-4 text-right">
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={() => handleSync(src._id)}
                            disabled={isSyncing}
                            className="text-gray-400 hover:text-amber-400 hover:bg-amber-500/10 p-2 rounded-lg transition-all disabled:opacity-40 disabled:cursor-wait"
                            data-testid={`epg-sync-btn-${src._id}`}
                            title="Yenile (manuel sync)"
                          >
                            <RefreshCw size={16} className={isSyncing ? 'animate-spin' : ''} />
                          </button>
                          <button
                            onClick={() => handleDelete(src._id)}
                            className="text-gray-500 hover:text-red-500 hover:bg-red-500/10 p-2 rounded-lg transition-all"
                            data-testid={`epg-delete-btn-${src._id}`}
                            title="Sil"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {showAdd && (
          <div className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-[100] p-4" data-testid="epg-add-modal">
            <div className="bg-slate-900 border border-slate-800 w-full max-w-xl rounded-2xl p-8 shadow-2xl">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-white flex items-center gap-2">
                  <CalendarDays className="text-amber-500" /> Yeni EPG Kaynağı
                </h3>
                <button onClick={() => setShowAdd(false)} className="text-gray-400 hover:text-white" data-testid="epg-add-modal-close"><X size={20} /></button>
              </div>
              <form onSubmit={handleAdd} className="space-y-4">
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase ml-1">Kaynak Adı</label>
                  <input
                    required
                    placeholder="Örn: Türkiye Ulusal EPG"
                    className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-3.5 text-white focus:border-amber-500 outline-none transition-all"
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    data-testid="epg-name-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase ml-1">XMLTV URL</label>
                  <input
                    required
                    placeholder="https://example.com/epg.xml"
                    className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-3.5 text-white focus:border-amber-500 outline-none transition-all font-mono text-sm"
                    value={form.url}
                    onChange={e => setForm({ ...form, url: e.target.value })}
                    data-testid="epg-url-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase ml-1">Tip</label>
                  <select
                    className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-3.5 text-white focus:border-amber-500 outline-none appearance-none cursor-pointer"
                    value={form.type}
                    onChange={e => setForm({ ...form, type: e.target.value })}
                    data-testid="epg-type-select"
                  >
                    {TYPE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                </div>
                <div className="flex gap-4 pt-4">
                  <button type="button" onClick={() => setShowAdd(false)} className="flex-1 bg-slate-800 text-white font-bold py-4 rounded-xl hover:bg-slate-700 transition-colors">İptal</button>
                  <button type="submit" className="flex-1 bg-amber-500 text-black font-bold py-4 rounded-xl hover:bg-amber-600 transition-colors shadow-lg shadow-amber-500/20" data-testid="epg-save-btn">Kaydet</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
